import React, { useEffect, useRef, useState } from "react";
import {
  useMediaQuery,
  Box,
  Theme,
  useTheme,
  Fab,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import TitleBar from "../../../../components/layout/TitleBar";
import AddIcon from '@mui/icons-material/Add';
import { USERS_CREATE_PATH, USERS_EDIT_PATH } from '../../../../constants/routes';
import styled from "@emotion/styled/macro";
import {
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridFilterItem,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableGrid from "../../../../components/TableGrid";
import SettingsIcon from '@mui/icons-material/Settings';
import { getUsers } from "../../../../services/userService";
import { User, Users } from "../../../../models/User";
import { Licenses } from "../../../../models/Licenses";
import IndividualSearch from "../../../../components/IndividualSearch";
import { getFormattedDate } from "../../../../utils/utils";
import { Link } from "react-router-dom";
import Loader from "../../../../components/Loader";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

const DataWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    height: 100%;
    .MuiDataGrid-columnHeaderTitle.icon {
        flex-grow: 0 !important;
        & > svg {
          margin-top: 50%;
        }
    }
    .is-deleted .MuiDataGrid-cell {
      font-style: italic;
      color: ${({ theme }: { theme: Theme }) => theme.palette.error.light};
    }
`;

const SwitchWrapper = styled(FormControlLabel)`
  & > .MuiSwitch-root .MuiSwitch-thumb {
    box-shadow: -2px 2px 3px rgba(0,0,0,0.2);
  }
  & > .MuiTypography-root {
    font-style: italic;
  }
  ${({ theme }: { theme: Theme }) => theme.breakpoints.down("xs")} {
    margin: ${({ theme }: { theme: Theme }) => theme.spacing(5)} 0px 10px auto;
  }
`;

const EditButton = styled(Link)`
    text-decoration: none;
    text-transform: uppercase;
    font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeightLight};
    color: ${({ theme }: { theme: Theme }) => theme.palette.primary.main};
    font-size: 10px;
    display: flex;
    align-items: center;
    svg {
      margin-right: 5px;
      width: 18px;
    }
`;

const ButtonIcon = styled(IconButton)`
    padding: 3px;
    color: ${({ theme }: { theme: Theme }) => theme.palette.primary.contrastText};
    &.state1 {
      background-color: ${({ theme }: { theme: Theme }) => theme.palette.primary.contrastText};
      & > svg {
        color: ${({ theme }: { theme: Theme }) => theme.palette.primary.main};
      }
    }
    &.state2 {
      background-color: ${({ theme }: { theme: Theme }) => theme.palette.text.primary};
      & > svg {
        color: ${({ theme }: { theme: Theme }) => theme.palette.primary.contrastText};
      }
    }
    & > svg {
      transform: none !important;
    }
`;

const HeaderFlex = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    & > button {
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
      & > svg {
        color: #fff;
        transform: rotate(-90deg);
        opacity: 0.5;
        &.desc {
          transform: rotate(90deg);
        }
      }
    }
    &:hover {
      & > button {
        opacity: 1;
      }
    }
`;

const ListUsers = () => {
  const [dataRows, setDataRows] = useState<Users>({ count: 0, users: [] });
  const [filterBy, setFilterBy] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(0);

  const buttonIconRef = useRef<HTMLButtonElement>(null);

  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  const isBiggerSize = useMediaQuery(theme.breakpoints.down('lg'));
  const isTabletSize = useMediaQuery(theme.breakpoints.down('md'));
  const isMobileSize = useMediaQuery(theme.breakpoints.down('xs'));

  const breadcrumbs = [{ text: t("Users") }];
  const pageSize = 8;

  const deletedIndex: number = (filterBy.some(f => f.key === "deleted")) ? filterBy.findIndex(f => f.key === "deleted") : -1;
  const nameIndex: number = (filterBy.some(f => f.key === "name")) ? filterBy.findIndex(f => f.key === "name") : -1;
  const emailIndex: number = (filterBy.some(f => f.key === "email")) ? filterBy.findIndex(f => f.key === "email") : -1;
  const statusIndex: number = (filterBy.some(f => f.key === "status")) ? filterBy.findIndex(f => f.key === "status") : -1;
  const entityIndex: number = (filterBy.some(f => f.key === "entity")) ? filterBy.findIndex(f => f.key === "entity") : -1;
  const loginIndex: number = (filterBy.some(f => f.key === "login")) ? filterBy.findIndex(f => f.key === "login") : -1;
  const isEnabledIndex: number = (filterBy.some(f => f.key === "active")) ? filterBy.findIndex(f => f.key === "active") : -1;
  const isValidated: number = (filterBy.some(f => f.key === "isValidated")) ? filterBy.findIndex(f => f.key === "isValidated") : -1;

  useEffect(() => {
    let active = true;

    if (!loading)
      return undefined;

    (async () => {
      try {
        let usersRows = await getUsers(undefined, (currentPage * pageSize), pageSize, filterBy);

        usersRows.users.forEach(
          (userRow: User, index: number) => {
            userRow.id = index + 1;
          });

        if (active) {
          setDataRows(usersRows);
          setLoading(false);
        }

      } catch (error) {
        if (active)
          setLoading(false);
      }
    })();

    return () => { active = false };

  }, [loading, currentPage, filterBy]);

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);

    (async () => {
      let queriedData = await getUsers(searchValue, (currentPage * pageSize), pageSize, filterBy);

      queriedData.users.forEach((userRow: User, index: number) => {
        userRow.id = index + 1;
      });

      setDataRows(queriedData);
    })();
  };

  function getEntity(license: Licenses) {
    if (license && license?.isActive) {
      let entity = dataRows.users.find(({ entity }) => entity?.id === license.entityId);

      return entity ? entity.entity?.name : t("Not Associated");
    }

    return t("Not Associated");
  }

  const checkStatus = [
    {
      label: 'Check',
      value: 'check',
      getApplyFilterFn: (filterItem: GridFilterItem, column: GridColDef) => {
        if (!filterItem.columnField || (filterItem.value === 2) || !filterItem.operatorValue) {
          return null;
        }
        return (params: GridCellParams): boolean => {
          return Number(params.value) === filterItem.value;
        };
      }
    }
  ];

  const handleFilter = (filterName: string, filterValue: any) => {
    const auxFilterBy = [...filterBy];
    
    if ( filterName !== "deleted" && filterName !== "isValidated" && filterName !== "active") {
      // only allow one column sort
      if (nameIndex !== -1) { auxFilterBy.splice(nameIndex, 1); }
      if (emailIndex !== -1) { auxFilterBy.splice(emailIndex, 1); }
      if (statusIndex !== -1) { auxFilterBy.splice(statusIndex, 1); }
      if (entityIndex !== -1) { auxFilterBy.splice(entityIndex, 1); }
      if (loginIndex !== -1) { auxFilterBy.splice(loginIndex, 1); }
    }

    if (filterValue !== "") {
      if (auxFilterBy.length > 0 && auxFilterBy.some(f => (f.key === filterName))) {
        const filterIndex = auxFilterBy.findIndex(f => (f.key === filterName));
        auxFilterBy[filterIndex].value = filterValue;
      } else {
        auxFilterBy.push(
          {
            key: filterName,
            value: filterValue
          }
        );
      }
    }

    setFilterBy(auxFilterBy);
    setCurrentPage(0);
    setLoading(true);
  };

  const getSortableState = (filterIndex: number) => {
    let filterVal: string = '';

    if (filterIndex > -1) {
      if (filterBy[filterIndex].value === "asc") {
        filterVal = "desc";
      } else if (filterBy[filterIndex].value === "desc") {
        filterVal = "";
      } else {
        filterVal = "asc";
      }
    } else {
      filterVal = "asc";
    }

    return filterVal;
  };

  const columns: GridColDef[] = [
    {
      field: 'lastName',
      headerName: t('Name'),
      disableColumnMenu: true,
      filterable: false,
      hide: true
    },
    {
      field: 'firstName',
      headerName: t('Name'),
      disableColumnMenu: true,
      filterable: false,
      flex: 0.5,
      sortable: false,
      hide: isTabletSize ? true : false,
      valueGetter: (params: GridValueGetterParams<string>) => (
        `${params.row.firstName} ${params.row.lastName}`
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <HeaderFlex
          className="MuiDataGrid-columnHeaderTitle"
          data-mui-internal-clone-element="true"
        >
          {params.colDef.headerName}
          <IconButton
            size="small"
            onClick={
              () => {
                const nameVal = getSortableState(nameIndex);
                handleFilter("name", nameVal);
              }
            }
          >
            {
              (nameIndex === -1 || (nameIndex > -1 && filterBy[nameIndex].value === "")) ?
                (
                  <CompareArrowsIcon
                    fontSize="small"
                  />
                ) :
                (nameIndex > -1 && filterBy[nameIndex].value === "asc") ?
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                    />
                  ) :
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                      className="desc"
                    />
                  )
            }
          </IconButton>
        </HeaderFlex>
      )
    },
    {
      field: 'email',
      headerName: t('E-mail'),
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      flex: isMobileSize ? 1 : 0.5,
      renderHeader: (params: GridColumnHeaderParams) => (
        <HeaderFlex
          className="MuiDataGrid-columnHeaderTitle"
          data-mui-internal-clone-element="true"
        >
          {params.colDef.headerName}
          <IconButton
            size="small"
            onClick={
              () => {
                const emailVal = getSortableState(emailIndex);
                handleFilter("email", emailVal);
              }
            }
          >
            {
              (emailIndex === -1 || (emailIndex > -1 && filterBy[emailIndex].value === "")) ?
                (
                  <CompareArrowsIcon
                    fontSize="small"
                  />
                ) :
                (emailIndex > -1 && filterBy[emailIndex].value === "asc") ?
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                    />
                  ) :
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                      className="desc"
                    />
                  )
            }
          </IconButton>
        </HeaderFlex>
      )
    },
    {
      field: 'deletionDate',
      headerName: t('Deleted Date'),
      disableColumnMenu: true,
      filterable: false,
      hide: true
    },
    {
      field: 'isEnabled',
      headerName: t('Status'),
      disableColumnMenu: true,
      filterable: true,
      sortable: false,
      filterOperators: checkStatus,
      minWidth: 150,
      type: 'boolean',
      align: 'left',
      hide: isMobileSize ? true : false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <HeaderFlex
          className="MuiDataGrid-columnHeaderTitle"
          data-mui-internal-clone-element="true"
        >
          {params.colDef.headerName}
          < ButtonIcon
            theme={theme}
            className={
              isEnabledIndex === -1 ?
                "" :
                (
                  filterBy[isEnabledIndex].value ? "state1" : "state2"
                )
            }
            onClick={
              () => {
                const isEnabledValue: boolean | null = isEnabledIndex > -1 ? filterBy[isEnabledIndex].value : null;

                if (isEnabledValue !== null && !isEnabledValue) {
                  handleFilter("active", '');
                } else {
                  handleFilter("active", !isEnabledValue);
                }
              }
            }
            ref={buttonIconRef}
          >
            <FilterListIcon sx={{ width: 16, height: 'auto' }} />
          </ButtonIcon>
          <IconButton
            size="small"
            onClick={
              () => {
                const statusVal = getSortableState(statusIndex);
                handleFilter("status", statusVal);
              }
            }
          >
            {
              (statusIndex === -1 || (statusIndex > -1 && filterBy[statusIndex].value === "")) ?
                (
                  <CompareArrowsIcon
                    fontSize="small"
                  />
                ) :
                (statusIndex > -1 && filterBy[statusIndex].value === "asc") ?
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                    />
                  ) :
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                      className="desc"
                    />
                  )
            }
          </IconButton>
        </HeaderFlex>
      ),
      renderCell: (params: GridRenderCellParams<string>) => (
        <>{params.row.isEnabled ? t("Active") : t("Deactive")}</>
      ),
    },
    {
      field: 'lastLogin',
      headerName: t('Login at'),
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      minWidth: 120,
      hide: isBiggerSize ? true : false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <HeaderFlex
          className="MuiDataGrid-columnHeaderTitle"
          data-mui-internal-clone-element="true"
        >
          {params.colDef.headerName}
          <IconButton
            size="small"
            onClick={
              () => {
                const loginVal = getSortableState(loginIndex);
                handleFilter("login", loginVal);
              }
            }
          >
            {
              (loginIndex === -1 || (loginIndex > -1 && filterBy[loginIndex].value === "")) ?
                (
                  <CompareArrowsIcon
                    fontSize="small"
                  />
                ) :
                (loginIndex > -1 && filterBy[loginIndex].value === "asc") ?
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                    />
                  ) :
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                      className="desc"
                    />
                  )
            }
          </IconButton>
        </HeaderFlex>
      ),
      valueGetter: (params: GridValueGetterParams<string>) => (
        getFormattedDate(params.row.lastLogin) || t("Never")
      ),
    },
    {
      field: 'license',
      headerName: t('Entity'),
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      minWidth: 170,
      hide: isMobileSize ? true : false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <HeaderFlex
          className="MuiDataGrid-columnHeaderTitle"
          data-mui-internal-clone-element="true"
        >
          {params.colDef.headerName}
          <IconButton
            size="small"
            onClick={
              () => {
                const entityVal = getSortableState(entityIndex);
                handleFilter("entity", entityVal);
              }
            }
          >
            {
              (entityIndex === -1 || (entityIndex > -1 && filterBy[entityIndex].value === "")) ?
                (
                  <CompareArrowsIcon
                    fontSize="small"
                  />
                ) :
                (entityIndex > -1 && filterBy[entityIndex].value === "asc") ?
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                    />
                  ) :
                  (
                    <ArrowRightAltIcon
                      fontSize="small"
                      className="desc"
                    />
                  )
            }
          </IconButton>
        </HeaderFlex>
      ),
      valueGetter: (params: GridValueGetterParams<string>) => (
        getEntity(params.row.license)
      ),
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      minWidth: isMobileSize ? 80 : 105,
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box
          className={
            isMobileSize ?
              "MuiDataGrid-columnHeaderTitle icon" :
              "MuiDataGrid-columnHeaderTitle"
          }
          aria-label=""
          data-mui-internal-clone-element="true"
        >
          {
            isMobileSize ?
              <SettingsIcon /> :
              params.colDef.headerName
          }
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<string>) => (
        params.row.deletionDate !== null ?
          <>{t("Deleted")}</>
          : <EditButton theme={theme} to={USERS_EDIT_PATH.replace(":email", params.row.email)}>
            <EditIcon />
            {!isMobileSize && t("Edit")}
          </EditButton>
      ),
    }
  ];

  const getCurrentPage = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet title={t("Users")} />
      <DataWrapper
        theme={theme}
        sx={{
          ".MuiDataGrid-main": {
            height: dataRows.users.length >= pageSize ?
              `${(pageSize + 1) * 40}px` :
              dataRows.users.length > 0 ?
                `${(dataRows.users.length + 1) * 40}px` :
                "auto"
          }
        }}
      >
        <TitleBar title={t('Users')} breadcrumbs={breadcrumbs} />
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            alignItems: "center",
            columnGap: "10px",
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "end",
              columnGap: 0,
            }
          }}
        >
          <SwitchWrapper
            theme={theme}
            control={
              <Switch
                color="secondary"
                checked={(isValidated > -1) ? filterBy[isValidated].value : false}
                onChange={
                  (event) => {
                    handleFilter("isValidated", event.target.checked)
                  }
                }
              />
            }
            label={t("Show only not validated users?")}
          />
          <SwitchWrapper
            theme={theme}
            control={
              <Switch
                color="secondary"
                checked={(deletedIndex > -1) ? filterBy[deletedIndex].value : false}
                onChange={
                  (event) => {
                    handleFilter("deleted", event.target.checked)
                  }
                }
              />
            }
            label={t("Show deleted users?")}
          />
        </Box>
        <TableGrid
          rows={dataRows.users}
          rowCount={dataRows.count}
          columns={columns}
          currentPage={currentPage}
          setPage={getCurrentPage}
          pageSize={pageSize}
          filterBy={0}
          customFilterModel={[]}
          rowsClassName={(params: GridRowParams) => (params.row.deletionDate != null) ? "is-deleted" : null}
          toolbar={IndividualSearch}
          toolbarProps={{
            text: <>
              {t("Search users by terms like")}{" "}
              <b>{t("NAME")}</b>{" "}{t("or")}{" "}
              <b>{t("E-MAIL")}</b>
            </>,
            value: searchText,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => requestSearch(event.target.value),
            clearSearch: () => requestSearch(''),
          }} />
      </DataWrapper>
      <Fab
        variant="extended"
        size="medium"
        color="primary"
        aria-label="add"
        href={USERS_CREATE_PATH}
        sx={{
          textTransform: 'uppercase',
          position: 'fixed',
          right: theme.spacing(10),
          bottom: theme.spacing(20),
          [theme.breakpoints.down('xs')]: {
            padding: 0,
            bottom: theme.spacing(25),
            svg: {
              margin: 0
            }
          }
        }}>
        <AddIcon sx={{ mr: 1 }} />
        {!isMobileSize && t("Users")}
      </Fab>
    </>
  );
};

export default ListUsers;